import { useCallback, useContext, useEffect, useMemo, useRef, useSyncExternalStore } from 'react'
import {
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  useStore,
} from '@ea-lab/reactive-json'
import { BuilderContext } from '../BuilderContextInstance'

/**
 * DocumentPreference — miroir « clés du store → attributs de <html> », conservé
 * d'une page à l'autre dans le stockage local du visiteur.
 *
 * POURQUOI DU JS (et rien de plus). Une PRÉFÉRENCE D'AFFICHAGE — taille du
 * texte, contraste renforcé, inversion sombre, interlignage — ne gouverne pas le
 * bloc qui porte ses commandes : elle gouverne la PAGE ENTIÈRE. Or chaque bloc
 * est monté dans son propre `ReactiveJsonRoot` (`BuilderBlockMount`) : le `~~.`
 * du bloc d'en-tête ne voit jamais le store d'un voisin, et aucun `hide` /
 * `setData` ne peut donc habiller le reste du document. Deuxième irréductible :
 * les liens du site sont des `<a>` ordinaires (`ResolvedLink`), donc chaque
 * navigation RECHARGE la page et repart des valeurs par défaut du rjBuild — une
 * préférence d'accessibilité qui se perd au premier lien ne sert à rien.
 *
 * Écrire un attribut sur `document.documentElement` et lire/écrire
 * `localStorage` sont deux API du navigateur qu'aucun rjBuild n'exprime. C'est
 * tout ce que fait ce composant : il ne rend RIEN, ne connaît aucune préférence
 * en particulier, et ne décide d'aucun style. L'EFFET VISUEL reste entièrement
 * déclaratif — des règles CSS `html[data-…="true"] …` dans la feuille du bloc —,
 * et les commandes restent des boutons YAML à `setData`.
 *
 * Usage YAML :
 *   data:
 *     a11yText: normal
 *     a11yContrast: "false"
 *   renderView:
 *     - type: DocumentPreference
 *       storageKey: sdis51-a11y          # sans lui : aucune conservation
 *       documentAttributes:
 *         data-a11y-text: a11yText       # attribut de <html> ← clé du store
 *         data-a11y-contrast: a11yContrast
 *
 * ABONNEMENT AU MAGASIN — le même piège que `UrlSync` : un composant monté comme
 * plugin lit le store en accès NON RÉACTIF, et resterait figé après le premier
 * rendu (le clic écrirait bien dans le store, l'attribut ne bougerait jamais,
 * sans la moindre erreur). On s'abonne donc au `DataStore` comme le fait le
 * moteur (`useSyncExternalStore` + `store.subscribe(path)`), avec un instantané
 * en CHAÎNE : un objet neuf à chaque appel ferait boucler le hook.
 *
 * MODE ÉDITION — inerte, délibérément. Le canevas du builder est le MÊME
 * document : une inversion sombre ou un grossissement de la racine repeindrait
 * l'outil d'édition lui-même. Rien n'est lu, rien n'est écrit, rien n'est posé
 * tant que `isEditMode` est vrai (même parti pris que `Carousel`).
 */

/** Les seules valeurs qu'on accepte de relire du stockage local (données d'un tiers : le visiteur). */
const isScalar = (v) => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'

export const DocumentPreference = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const builderCtx = useContext(BuilderContext)
  const store = useStore()
  const isEditMode = !!builderCtx?.isEditMode

  const storageKey = evaluateTemplateValue({
    valueToEvaluate: props.storageKey,
    globalDataContext,
    templateContext,
  })
  const rawMap = evaluateTemplateValue({
    valueToEvaluate: props.documentAttributes,
    globalDataContext,
    templateContext,
  })

  // [[attribut, clé du store], …] — signature stable pour les dépendances des
  // hooks ci-dessous, qui ne peuvent pas porter sur un objet neuf à chaque rendu.
  const pairsSig = JSON.stringify(
    rawMap && typeof rawMap === 'object' && !Array.isArray(rawMap)
      ? Object.entries(rawMap).filter(([attr, key]) => attr && typeof key === 'string' && key)
      : [],
  )
  const pairs = useMemo(() => JSON.parse(pairsSig), [pairsSig])

  // ---- Reprise de la préférence conservée -------------------------------
  // Une seule fois par montage, AVANT tout miroir : sans ce verrou, l'écriture
  // dans le store relancerait le rendu, qui relirait le stockage, indéfiniment.
  const seeded = useRef(false)
  useEffect(() => {
    if (isEditMode || seeded.current || !storageKey) return
    seeded.current = true
    let stored = null
    try {
      stored = JSON.parse(window.localStorage.getItem(String(storageKey)) ?? 'null')
    } catch {
      // Stockage refusé (navigation privée, réglage du navigateur) ou contenu
      // illisible : la préférence ne se conserve pas, les commandes marchent
      // quand même. Rien à signaler au visiteur.
      return
    }
    if (!stored || typeof stored !== 'object') return
    pairs.forEach(([, key]) => {
      if (isScalar(stored[key])) store.set(key, String(stored[key]))
    })
  }, [isEditMode, storageKey, pairs, store])

  // ---- Miroir store → <html> + stockage ---------------------------------
  const subscribe = useCallback((onChange) => {
    const unsubs = pairs.map(([, key]) => store.subscribe(key, onChange))
    return () => unsubs.forEach((u) => u())
  }, [store, pairs])

  const getSnapshot = useCallback(
    () => JSON.stringify(pairs.map(([, key]) => store.get(key) ?? null)),
    [store, pairs],
  )

  const valuesSig = useSyncExternalStore(subscribe, getSnapshot)

  // L'instantané ne sert qu'à DÉCLENCHER ce miroir ; les valeurs sont relues
  // dans le store au moment de l'effet. Sinon, au montage, la reprise ci-dessus
  // écrirait la préférence conservée dans le store APRÈS que ce rendu-ci en a
  // figé les valeurs par défaut : on repeindrait la page en « normal », et on
  // écraserait la préférence dans le stockage, avant de se corriger au rendu
  // suivant. Un battement visible, et une perte si l'onglet se ferme entre les
  // deux.
  useEffect(() => {
    if (isEditMode) return
    const root = document.documentElement
    const kept = {}
    pairs.forEach(([attr, key]) => {
      const value = store.get(key)
      if (value == null || value === '') {
        root.removeAttribute(attr)
        return
      }
      root.setAttribute(attr, String(value))
      kept[key] = String(value)
    })
    if (storageKey) {
      try {
        window.localStorage.setItem(String(storageKey), JSON.stringify(kept))
      } catch {
        // Voir plus haut : un stockage indisponible ne doit rien casser.
      }
    }
    // Le bloc démonté (changement de route côté client), la page ne porte plus
    // les commandes : laisser les attributs figerait l'habillage sans moyen d'en
    // sortir.
    return () => pairs.forEach(([attr]) => root.removeAttribute(attr))
  }, [isEditMode, valuesSig, pairs, storageKey, store])

  return null
}
